/**
 * Making an Arabic name searchable.
 *
 * Firestore has no substring search: a query matches a whole field, a prefix,
 * or a member of an array. Names here are searched by any part — "أنس" as a
 * first name, "القليصي" as a family name — so each record carries its name
 * split into tokens and the query asks whether the array contains one.
 *
 * The alternative was filtering rows already loaded, which is what the first
 * version did. It found nobody outside the first page and looked broken, which
 * on a roster of 872 it effectively was.
 *
 * Lives in `core/` because two consumers must agree on it exactly: the import
 * that writes the tokens and the query that reads them. A second copy would
 * drift and the search would quietly stop finding people.
 */

/**
 * Folds the spellings that vary between one clerk and another.
 *
 * Someone typing their own name and someone typing it into a spreadsheet
 * differ on hamza, on ya, and on ta-marbuta constantly. Folding them costs
 * nothing — no two distinct Yemeni names differ only by these — and not
 * folding them means "احمد" never finds "أحمد".
 */
export function foldArabic(value: string): string {
  return value
    .trim()
    .replace(/[أإآٱ]/g, 'ا')
    .replace(/ى/g, 'ي')
    .replace(/ة/g, 'ه')
    .replace(/[ً-ْٰ]/g, '') // diacritics
    .replace(/ـ/g, '') // tatweel
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

/** Words too short to narrow anything, and the connectors between names. */
const IGNORED = new Set(['بن', 'بنت', 'ابن', 'ال', 'عبد']);

/**
 * The searchable tokens of a name.
 *
 * "عبد" is dropped as a standalone word but kept joined: "عبدالله" is a name,
 * "عبد" alone matches half the roster.
 */
export function nameTokens(fullName: string): readonly string[] {
  const words = foldArabic(fullName)
    .split(' ')
    .map((word) => word.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter((word) => word.length > 1 && !IGNORED.has(word));

  return [...new Set(words)];
}

/** The single token a search box is asking about, or null if it asks nothing. */
export function toSearchToken(input: string): string | null {
  const tokens = nameTokens(input);
  // One term: `array-contains` takes a single value, and a two-word search is
  // answered by its rarer half — the family name — which the caller types.
  return tokens[0] ?? null;
}
